import React from 'react';
import PropTypes from 'prop-types';

import {Box, useInView} from 'views/components';
import MarkdownViewer from 'views/module/markdown/MarkdownViewer';

const STREAM_SEGMENT_CHARS = 2000;
// How far outside the visible chat an answer starts rendering, so scrolling rarely meets a placeholder.
const LAZY_RENDER_MARGIN = '1200px 0px';

const MemoizedMarkdownViewer = React.memo(MarkdownViewer);

// Splits text at blank lines outside code fences into finished segments and the unfinished end.
const splitFinishedMarkdown = (text) => {
  const segments = [];
  let segmentStart = 0;
  let lineStart = 0;
  let inFence = false;
  let lineEnd = text.indexOf('\n', lineStart);
  while (lineEnd !== -1) {
    const line = text.slice(lineStart, lineEnd);
    if (/^\s*(```|~~~)/.test(line)) {
      inFence = !inFence;
    }
    lineStart = lineEnd + 1;
    if (!inFence && line.trim() === '' && lineStart - segmentStart >= STREAM_SEGMENT_CHARS) {
      segments.push(text.slice(segmentStart, lineStart));
      segmentStart = lineStart;
    }
    lineEnd = text.indexOf('\n', lineStart);
  }
  return {segments, tail: text.slice(segmentStart)};
};

// Finished segments never change, so a streaming refresh or the final answer only re-parses the unfinished end.
const SegmentedMarkdown = ({text}) => {
  const {segments, tail} = splitFinishedMarkdown(text);
  return (
    <>
      {segments.map((segment, i) => (
        <MemoizedMarkdownViewer key={i} value={segment} />
      ))}
      <MarkdownViewer value={tail} />
    </>
  );
};

SegmentedMarkdown.propTypes = {
  text: PropTypes.string.isRequired,
};

// Rough rendered height, fitted to real Chanel answers, so the scroll bar stays close before an answer renders.
const estimateMarkdownHeight = (text) => (
  text ? Math.round(text.length * 0.25 + (text.match(/\n/g) || []).length * 10 + 50) : 0
);

// Renders the answer once it comes near the visible part of root, then keeps it rendered.
const LazyMarkdown = ({value, root}) => {
  const {ref, inView} = useInView({root, rootMargin: LAZY_RENDER_MARGIN, triggerOnce: true, fallbackInView: true});
  return (
    <Box ref={ref} sx={inView ? undefined : {minHeight: `${estimateMarkdownHeight(value)}px`}}>
      {inView && <SegmentedMarkdown text={value} />}
    </Box>
  );
};

LazyMarkdown.propTypes = {
  value: PropTypes.string.isRequired,
  root: PropTypes.object,
};

export default LazyMarkdown;
